import { Link } from "react-router-dom";
import { site } from "../data/site";

/**
 * Official SRM brand mark.
 *
 *   • Source: /assets/srm-logo.png (transparent PNG, original proportions preserved).
 *   • `variant="dark"`  → on light backgrounds (header).      Renders the logo as-is.
 *   • `variant="light"` → on dark navy backgrounds (footer).  Sits on a small white plate
 *                          so the official brand colours stay intact (no recolouring).
 *   • `size` controls render height; width auto-scales to keep proportions.
 */
const LOGO_SRC = "/assets/srm-logo.png";

// Logo natural aspect ratio is 2.07:1 (1342×649). Use object-contain and w-auto
// so the image never gets stretched. Heights below allow the tagline beneath
// the SRM wordmark to render crisply at every breakpoint.
const HEIGHTS = {
    sm: "h-10",
    md: "h-14 lg:h-[64px] xl:h-[71px]",
    lg: "h-20 lg:h-24",
    xl: "h-40 sm:h-52 lg:h-64",
};

export default function Logo({ variant = "dark", size = "md", className = "" }) {
    const isLight = variant === "light";
    const heightClass = HEIGHTS[size] || HEIGHTS.md;

    const img = (
        <img
            src={LOGO_SRC}
            alt={`${site.business.fullName} logo`}
            className={`${heightClass} w-auto max-w-none object-contain select-none`}
            draggable={false}
        />
    );

    const inner = isLight ? (
        <span className="inline-flex items-center justify-center rounded-md bg-white p-2.5 shadow-sm">
            {img}
        </span>
    ) : (
        img
    );

    // `size="xl"` is for hero brand statement — render the logo on the
    // same white plate so the official brand colours stay readable on
    // dark backgrounds without any recolouring.
    if (size === "xl") {
        return (
            <div
                data-testid="srm-wordmark-hero"
                className={`inline-flex items-center ${className}`}
                aria-label={`${site.business.name} — ${site.business.fullName}`}
            >
                {isLight ? (
                    <span className="inline-flex items-center justify-center rounded-md bg-white px-5 py-4 shadow-sm">
                        {img}
                    </span>
                ) : (
                    img
                )}
            </div>
        );
    }

    return (
        <Link
            to="/"
            data-testid="srm-logo"
            className={`inline-flex items-center ${className}`}
            aria-label={`${site.business.name} — ${site.business.fullName}`}
        >
            {inner}
        </Link>
    );
}
